export const FLIP_MS = 300;
export const FLIP_STAGGER_MS = 120;
export const FLIP_TILES = 5;
export const TICK_DELAY_MS = 80;
export const TICK_MS = 420;
export const TICK_STEPS = 10;
export const BADGE_MS = 240;
export const ROLL_KEY_TTL_MS = 20_000;
export const CARD_KEPT_MS = 10_000;
export const CARD_FADE_MS = 600;
export const THROWN_DICE = 5;
export const THROW_STAGGER_MS = 70;
export const THROW_MS = 820;
export const THROW_TOUCHES = [0.62, 0.9] as const;
export const LANES = 3;
// One card per lane, so the oldest card's drop frees a lane for the next roll.
export const CARDS_KEPT = LANES;

export type ThrownDie = { leaveAt: number; touchAt: [number, number]; landAt: number };

// revealAt[n] is when tile n finishes turning over; doneAt is when the
// schedule's own motion ends, and a card plays on to its total and badge —
// a thrown die holds once landed.
type Timeline = { revealAt: number[]; tickAt: number; doneAt: number };
export type ThrowSchedule = Timeline & { mode: "throw"; dice: ThrownDie[] };
export type Schedule = (Timeline & { mode: "flip" | "instant" }) | ThrowSchedule;

export function flipSchedule(count: number): Schedule {
	const revealAt = Array.from({ length: count }, (_, n) => FLIP_MS + FLIP_STAGGER_MS * Math.min(n, FLIP_TILES - 1));
	const tickAt = (revealAt.at(-1) ?? 0) + TICK_DELAY_MS;
	return { mode: "flip", revealAt, tickAt, doneAt: tickAt };
}

export function instantSchedule(): Schedule {
	return { mode: "instant", revealAt: [], tickAt: 0, doneAt: 0 };
}

export function throwSchedule(count: number): ThrowSchedule {
	const dice = Array.from({ length: Math.min(count, THROWN_DICE) }, (_, n): ThrownDie => {
		const leaveAt = THROW_STAGGER_MS * n;
		const [first, second] = THROW_TOUCHES.map((touch) => leaveAt + Math.round(THROW_MS * touch));
		return { leaveAt, touchAt: [first, second], landAt: leaveAt + THROW_MS };
	});
	const revealAt = Array.from({ length: count }, (_, n) => dice[Math.min(n, dice.length - 1)].landAt);
	const tickAt = (revealAt.at(-1) ?? 0) + TICK_DELAY_MS;
	return { mode: "throw", dice, revealAt, tickAt, doneAt: tickAt };
}

export type ThrowMoment = { at: number; name: "throw" | "bounce" | "land"; index: number; bounce?: number };

// When each dice-landing:throw, :bounce and :land fires.
export function throwMoments(schedule: Schedule): ThrowMoment[] {
	if (schedule.mode !== "throw") return [];
	return schedule.dice
		.flatMap(({ leaveAt, touchAt, landAt }, index): ThrowMoment[] => [
			{ at: leaveAt, name: "throw", index },
			...touchAt.map((at, k): ThrowMoment => ({ at, name: "bounce", index, bounce: k + 1 })),
			{ at: landAt, name: "land", index },
		])
		.sort((a, b) => a.at - b.at);
}

// One lane per roll in the air, so dice never land on each other. take()
// starts past the lane it last took rather than always the lowest free one,
// so with dice lingering well past landing, back-to-back rolls spread across
// all the lanes before any of them reuses a spot.
export function createLanes(count = LANES) {
	const busy = new Set<number>();
	let last = -1;
	return {
		take() {
			const lane = Array.from({ length: count }, (_, k) => (last + 1 + k) % count).find((k) => !busy.has(k));
			if (lane === undefined) return null;
			busy.add(lane);
			last = lane;
			return lane;
		},
		free(lane: number) {
			busy.delete(lane);
		},
	};
}

export type Lanes = ReturnType<typeof createLanes>;

export function chooseSchedule(
	count: number,
	{ reduced, throwing, lanes }: { reduced: boolean; throwing: boolean; lanes: Lanes },
): { lane: number; schedule: ThrowSchedule } | { lane: null; schedule: Schedule } {
	if (reduced) return { lane: null, schedule: instantSchedule() };
	const lane = throwing && count ? lanes.take() : null;
	if (lane === null) return { lane: null, schedule: flipSchedule(count) };
	return { lane, schedule: throwSchedule(count) };
}

// The values the total shows from the dice's sum to the total.
export function tickSteps(sum: number, modifier: number): number[] {
	if (!modifier) return [];
	const count = Math.min(Math.abs(modifier) + 1, TICK_STEPS);
	return Array.from({ length: count }, (_, k) => sum + Math.round((modifier * k) / (count - 1)));
}

export function rollKey({ name, notation, result }: { name: string; notation: string; result: string }) {
	return JSON.stringify([name, notation, result]);
}

// The rolls this board played at the click, so their broadcast plays nothing.
export function createRecentRolls({ ttl = ROLL_KEY_TTL_MS, now = Date.now }: { ttl?: number; now?: () => number } = {}) {
	let entries: { key: string; at: number }[] = [];
	const prune = () => {
		entries = entries.filter(({ at }) => now() - at < ttl);
	};
	const take = (key: string) => {
		prune();
		const index = entries.findIndex((entry) => entry.key === key);
		if (index === -1) return false;
		entries.splice(index, 1);
		return true;
	};

	return {
		remember(key: string) {
			prune();
			entries.push({ key, at: now() });
		},
		consume: take,
		forget(key: string) {
			take(key);
		},
	};
}
